import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  NumberInput,
  Paper,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCalendar,
  IconCamera,
  IconChevronLeft,
  IconCloudRain,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useDailyLog,
  useUpdateDailyLog,
  useDeleteDailyLog,
  type UpdateDailyLogData,
} from "@/hooks/useDailyLogs";
import { useProjectCostCodeOptions } from "@/hooks/useProjectCostCodes";
import { usePhotos } from "@/hooks/usePhotos";
import { PhotoCapture } from "@/components/PhotoCapture";
import { SyncStatusBadge } from "@/components/OfflineIndicator";

export const Route = createFileRoute(
  "/projects/$projectId/daily-logs/$logId" as any
)({
  component: DailyLogDetailPage,
});

const WEATHER_CONDITIONS = [
  "Clear",
  "Partly Cloudy",
  "Cloudy",
  "Light Rain",
  "Rain",
  "Heavy Rain",
  "Thunderstorm",
  "Snow",
  "Fog",
  "Windy",
];

function DailyLogDetailPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const logId = "logId" in params ? params.logId : "";
  const navigate = useNavigate();
  const { data, isLoading } = useDailyLog(projectId, logId);
  const { data: photosData, refetch: refetchPhotos } = usePhotos(projectId, logId);
  const { data: costCodeData, isLoading: costCodesLoading } = useProjectCostCodeOptions(projectId);
  const updateDailyLog = useUpdateDailyLog(projectId);
  const deleteDailyLog = useDeleteDailyLog(projectId);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  const [isEditing, setIsEditing] = useState(false);

  const log = data?.dailyLog;
  const photos = photosData?.photos || [];

  const form = useForm<{
    date: Date;
    temp: number;
    conditions: string;
    costCodeId: string;
    crewCount: number;
    workPerformed: string;
    issues: string;
  }>({
    initialValues: {
      date: log ? (() => {
        // Parse date as local timezone to avoid UTC conversion issues
        const parts = log.date.split("-").map(Number);
        const year = parts[0] ?? new Date().getFullYear();
        const month = parts[1] ?? 1;
        const day = parts[2] ?? 1;
        return new Date(year, month - 1, day);
      })() : new Date(),
      temp: log?.weather.temp || 70,
      conditions: log?.weather.conditions || "Clear",
      costCodeId: log?.costCodeId || "",
      crewCount: log?.crewCount || 1,
      workPerformed: log?.workPerformed || "",
      issues: log?.issues || "",
    },
    validate: {
      temp: (value) =>
        value < -50 || value > 150 ? "Temperature must be between -50 and 150°F" : null,
      crewCount: (value) =>
        value < 0 ? "Crew count must be at least 0" : null,
      workPerformed: (value) =>
        !value || value.trim().length === 0 ? "Work performed is required" : null,
    },
  });

  // Update form when log data loads
  if (log && !isLoading && !isEditing) {
    const currentDate = form.values.date;
    // Parse date as local timezone to avoid UTC conversion issues
    const parts = log.date.split("-").map(Number);
    const year = parts[0] ?? new Date().getFullYear();
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    const logDate = new Date(year, month - 1, day);
    if (currentDate.getTime() !== logDate.getTime()) {
      form.setValues({
        date: logDate,
        temp: log.weather.temp,
        conditions: log.weather.conditions,
        costCodeId: log.costCodeId || "",
        crewCount: log.crewCount,
        workPerformed: log.workPerformed,
        issues: log.issues || "",
      });
    }
  }

  const handleEdit = form.onSubmit(async (values) => {
    try {
      const updateData: UpdateDailyLogData = {
        date: values.date.toISOString().split("T")[0] || "",
        costCodeId: values.costCodeId || undefined,
        weather: {
          temp: values.temp,
          conditions: values.conditions,
        },
        crewCount: values.crewCount,
        workPerformed: values.workPerformed,
        issues: values.issues.trim().length > 0 ? values.issues : undefined,
      };

      await updateDailyLog.mutateAsync({ id: logId, data: updateData });

      notifications.show({
        title: "Success",
        message: "Daily log updated successfully!",
        color: "green",
      });

      setIsEditing(false);
      closeEdit();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update daily log",
        color: "red",
      });
    }
  });

  const handleDelete = async () => {
    try {
      await deleteDailyLog.mutateAsync(logId);

      notifications.show({
        title: "Success",
        message: "Daily log deleted successfully!",
        color: "green",
      });

      navigate({
        to: "/projects/$projectId/daily-logs",
        params: { projectId },
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete daily log",
        color: "red",
      });
    }
  };

  const handleBack = () => {
    navigate({
      to: "/projects/$projectId/daily-logs",
      params: { projectId },
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    openEdit();
  };

  const formatDate = (dateString: string) => {
    // Parse date as local timezone to avoid UTC conversion issues
    // dateString format is "YYYY-MM-DD"
    const parts = dateString.split("-").map(Number);
    const year = parts[0] ?? new Date().getFullYear();
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  if (!log) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="md" align="center">
          <Text size="lg" c="dimmed">
            Daily log not found
          </Text>
          <Button onClick={handleBack}>Back to Daily Logs</Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Button
              variant="subtle"
              leftSection={<IconChevronLeft size={16} />}
              onClick={handleBack}
            >
              Back to Daily Logs
            </Button>

            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="lg">
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item leftSection={<IconEdit size={16} />} onClick={handleEditClick}>
                  Edit Log
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={openDelete}
                >
                  Delete Log
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Header with date */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Group gap="xs" mb="xs">
                <IconCalendar size={24} stroke={1.5} />
                <Title order={2}>{formatDate(log.date)}</Title>
                {log.syncStatus && (
                  <SyncStatusBadge status={log.syncStatus} error={log.syncError} />
                )}
              </Group>
            </div>
          </Group>

          {/* Weather and crew info */}
          <Group gap="md">
            <Badge variant="light" size="lg" leftSection={<IconCloudRain size={18} />}>
              {log.weather.temp}°F - {log.weather.conditions}
            </Badge>
            <Badge variant="light" size="lg" leftSection={<IconUsers size={18} />}>
              {log.crewCount} crew
            </Badge>
            {log.costCode && (
              <Badge variant="light" size="lg" color="blue">
                {log.costCode} - {log.costCodeName}
              </Badge>
            )}
          </Group>

          {/* Work Performed */}
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Text fw={500} size="sm" c="dimmed">
                Work Performed
              </Text>
              <Text style={{ whiteSpace: "pre-wrap" }}>{log.workPerformed}</Text>
            </Stack>
          </Paper>

          {/* Issues (if present) */}
          {log.issues && (
            <Paper p="md" withBorder style={{ borderColor: "var(--mantine-color-orange-3)" }}>
              <Stack gap="sm">
                <Group gap="xs">
                  <Badge color="orange" variant="light">
                    Issues & Notes
                  </Badge>
                </Group>
                <Text style={{ whiteSpace: "pre-wrap" }}>{log.issues}</Text>
              </Stack>
            </Paper>
          )}

          {/* Photos Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group gap="xs">
                <IconCamera size={20} stroke={1.5} />
                <Text fw={500}>Photos</Text>
                {photos.length > 0 && (
                  <Badge variant="light" size="sm">
                    {photos.length}
                  </Badge>
                )}
              </Group>
              <PhotoCapture
                projectId={projectId}
                dailyLogId={logId}
                photos={photos}
                onPhotoAdded={() => refetchPhotos()}
                onPhotoDeleted={() => refetchPhotos()}
                maxPhotos={10}
                showCostCodeSelector={true}
              />
            </Stack>
          </Paper>

          {/* Metadata */}
          <Paper p="sm" withBorder style={{ background: "var(--mantine-color-gray-0)" }}>
            <Group gap="md">
              <Text size="xs" c="dimmed">
                Created: {new Date(log.createdAt).toLocaleString()}
              </Text>
              {log.syncedAt && (
                <Text size="xs" c="dimmed">
                  Synced: {new Date(log.syncedAt).toLocaleString()}
                </Text>
              )}
            </Group>
          </Paper>
        </Stack>
      </Paper>

      {/* Edit Modal */}
      <Modal
        opened={editOpened}
        onClose={() => {
          closeEdit();
          setIsEditing(false);
        }}
        title="Edit Daily Log"
        size="lg"
        centered
      >
        <form onSubmit={handleEdit}>
          <Stack gap="lg">
            {/* Date */}
            <DateInput
              label="Date"
              placeholder="Select date"
              {...form.getInputProps("date")}
              required
              size="md"
              maxDate={new Date()}
            />

            {/* Weather Section */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Text fw={500} size="sm">
                  Weather Conditions
                </Text>

                <Group grow>
                  <NumberInput
                    label="Temperature"
                    placeholder="70"
                    {...form.getInputProps("temp")}
                    required
                    suffix="°F"
                    size="md"
                    min={-50}
                    max={150}
                  />

                  <Select
                    label="Conditions"
                    placeholder="Select weather"
                    {...form.getInputProps("conditions")}
                    data={WEATHER_CONDITIONS}
                    required
                    size="md"
                    searchable
                  />
                </Group>
              </Stack>
            </Paper>

            {/* Primary Activity (Cost Code) - Optional */}
            <Select
              label="Primary Activity (Optional)"
              placeholder={
                costCodesLoading
                  ? "Loading cost codes..."
                  : (costCodeData?.options?.length ?? 0) === 0
                    ? "No cost codes assigned to this project"
                    : "Select the main work activity"
              }
              description={
                (costCodeData?.options?.length ?? 0) === 0 && !costCodesLoading
                  ? "To track work by activity, assign cost codes to this project in Budget or Settings"
                  : "Tag this log with the main work activity's cost code"
              }
              {...form.getInputProps("costCodeId")}
              data={costCodeData?.options || []}
              searchable
              clearable
              disabled={costCodesLoading || (costCodeData?.options?.length ?? 0) === 0}
              size="sm"
              styles={{
                label: {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "var(--mantine-color-dimmed)",
                },
                description: {
                  fontSize: "0.75rem",
                },
              }}
            />

            {/* Crew Count */}
            <NumberInput
              label="Crew Count"
              placeholder="Number of workers on site"
              {...form.getInputProps("crewCount")}
              required
              size="md"
              min={0}
            />

            {/* Work Performed */}
            <Textarea
              label="Work Performed"
              placeholder="Describe the work completed today..."
              {...form.getInputProps("workPerformed")}
              required
              size="md"
              minRows={6}
              autosize
            />

            {/* Issues (Optional) */}
            <Textarea
              label="Issues & Notes (Optional)"
              placeholder="Any problems, delays, or important notes..."
              {...form.getInputProps("issues")}
              size="md"
              minRows={4}
              autosize
            />

            {/* Action Buttons */}
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  closeEdit();
                  setIsEditing(false);
                }}
                disabled={updateDailyLog.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateDailyLog.isPending}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Delete Daily Log"
        centered
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this daily log? This action cannot be undone.
          </Text>
          <Paper p="sm" withBorder>
            <Text size="sm" fw={500}>
              {formatDate(log.date)}
            </Text>
            <Text size="sm" c="dimmed">
              {log.workPerformed.slice(0, 100)}...
            </Text>
          </Paper>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={closeDelete}
              disabled={deleteDailyLog.isPending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={deleteDailyLog.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
